import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { Lock, RefreshCw } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { useAuthorization } from '@/auth/hooks/useAuthorization';
import { useTramiteTareas } from '../hooks/useTramiteTareas';
import { TareaChecklistItem } from './TareaChecklistItem';
import type { TareasCountSource, TramiteTareaItem } from '../types';

interface TramiteTareasDialogProps {
  tramite: TareasCountSource | null;
  isOpen: boolean;
  onClose: () => void;
  /** Se llama tras cada mutación exitosa, para que la grilla/kanban refresque
   * sus propios contadores (ver TareasProgressCell). */
  onChanged?: () => void;
}

export const TramiteTareasDialog: React.FC<TramiteTareasDialogProps> = ({
  tramite,
  isOpen,
  onClose,
  onChanged,
}) => {
  const { canManageTareas } = useAuthorization();
  const [nuevaTarea, setNuevaTarea] = useState('');

  const puedeEditar = canManageTareas;

  const {
    tareas,
    resumen,
    isLoading,
    crearTarea,
    actualizarTarea,
    eliminarTarea,
    sincronizarBase,
    isGuardando,
  } = useTramiteTareas(tramite?.id, { enabled: isOpen, onChanged });

  if (!tramite) return null;

  const handleAgregar = async () => {
    const nombre = nuevaTarea.trim();
    if (!nombre) {
      toast.error('El nombre de la tarea es obligatorio');
      return;
    }

    try {
      await crearTarea({ nombre });
      setNuevaTarea('');
    } catch {
      toast.error('No se pudo agregar la tarea.');
    }
  };

  const handleToggle = async (tarea: TramiteTareaItem, completada: boolean) => {
    try {
      await actualizarTarea({ id: tarea.id, data: { completada } });
    } catch {
      toast.error('No se pudo actualizar la tarea.');
    }
  };

  const handleRenombrar = async (tarea: TramiteTareaItem, nombre: string) => {
    try {
      await actualizarTarea({ id: tarea.id, data: { nombre } });
    } catch {
      toast.error('No se pudo renombrar la tarea.');
    }
  };

  const handleEliminar = async (tarea: TramiteTareaItem) => {
    try {
      await eliminarTarea(tarea.id);
    } catch {
      toast.error('No se pudo eliminar la tarea.');
    }
  };

  const handleSincronizar = async () => {
    try {
      const antes = tareas.length;
      const payload = await sincronizarBase();
      const agregadas = payload.tareas.length - antes;
      toast.success(
        agregadas > 0 ? `Se cargaron ${agregadas} tarea(s) de la base.` : 'El checklist ya está al día con la base.'
      );
    } catch {
      toast.error('No se pudo cargar la base de tareas.');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Tareas — Escritura #{tramite.id}</DialogTitle>
          <DialogDescription>
            Checklist de tareas de esta escritura. Se puede agregar o eliminar tareas
            puntuales sin afectar a otras escrituras.
          </DialogDescription>
        </DialogHeader>

        {tramite.bloqueado && (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            <div className="flex items-center gap-1 font-medium">
              <Lock className="h-3.5 w-3.5" />
              Escritura bloqueada
            </div>
            {tramite.bloqueado_motivo && (
              <p className="mt-1 break-words text-xs">{tramite.bloqueado_motivo}</p>
            )}
          </div>
        )}

        <div className="mb-2">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">Progreso</span>
            <span className="text-sm text-gray-600">
              {resumen.completadas}/{resumen.total} ({Math.round(resumen.porcentaje)}%)
            </span>
          </div>
          <Progress value={resumen.porcentaje} className="h-2" />
        </div>

        {puedeEditar && (
          <div className="flex gap-2">
            <Input
              value={nuevaTarea}
              onChange={(event) => setNuevaTarea(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') void handleAgregar();
              }}
              placeholder="Agregar tarea…"
              aria-label="Nueva tarea"
              maxLength={255}
              disabled={isGuardando}
            />
            <Button onClick={() => void handleAgregar()} disabled={isGuardando}>
              Agregar
            </Button>
          </div>
        )}

        {isLoading ? (
          <p className="py-6 text-center text-sm text-muted-foreground">Cargando…</p>
        ) : tareas.length === 0 ? (
          <div className="py-6 text-center">
            <p className="mb-3 text-sm text-muted-foreground">
              Esta escritura todavía no tiene tareas cargadas.
            </p>
            {puedeEditar && (
              <Button variant="outline" onClick={() => void handleSincronizar()} disabled={isGuardando}>
                <RefreshCw className="mr-2 h-4 w-4" />
                Cargar tareas base
              </Button>
            )}
          </div>
        ) : (
          <>
            <ul className="space-y-2">
              {tareas.map((tarea) => (
                <TareaChecklistItem
                  key={tarea.id}
                  tarea={tarea}
                  onToggle={handleToggle}
                  onRenombrar={handleRenombrar}
                  onEliminar={handleEliminar}
                  disabled={!puedeEditar || isGuardando}
                />
              ))}
            </ul>
            {puedeEditar && (
              <Button
                variant="ghost"
                size="sm"
                className="w-fit text-muted-foreground"
                onClick={() => void handleSincronizar()}
                disabled={isGuardando}
              >
                <RefreshCw className="mr-2 h-3.5 w-3.5" />
                Cargar tareas base
              </Button>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TramiteTareasDialog;
